import { useGLTF } from "@react-three/drei";
import { useConfigurationOptionsContext } from "../../../../../app/providers/DoorOptionsProvider";
import { getFullAssetPath } from "../../../../../shared/utils/assets";
import { DoorComponentProps } from "../../Door";
import Beam from "../components/Beam";
import Glass from "../components/Glass";
import { DeIjsselLayout } from "../layouts/DeIjsselLayout";
import { DeMaasLayout } from "../layouts/DeMaasLayout";
import { DeWaalLayout } from "./DeWaalLayout";
import { deWaal2PanelCodes } from "entities/configurator/model/layoutMeta";
import Frame from "./Frame";
import PaneelBeam from "./PaneelBeam";
import PaneelDeMaas2DubbelBeam from "./PaneelDeMaas2DubbelBeam";
import { PaneelGLTFResult } from "./PaneelType";
import { PaneelVerticalLayout } from "../layouts/PaneelVerticalLayout";
import { deIjsselGeometry } from "../layouts/deIjsselGeometry";
import { type VerticalBeamPosition } from "entities/configurator/model/layoutMeta";

interface PaneelProps extends DoorComponentProps {
  verticalBeamPosition?: VerticalBeamPosition;
}

const panelSecondaryDoors = [
  "dubbeleschuifdeur2paneel",
  "dubbelescharnierdeur2paneel",
  "dubbel2paneel",
];

const panelMainLayouts = ["dewaal3dubbel", "dewaal4dubbel"];

const Paneel = ({
  materials,
  position,
  index = 0,
  frameSettings,
  rotation,
  rotateNewlayouts = false,
  verticalBeamPosition,
}: PaneelProps) => {
  const { nodes } = useGLTF(
    getFullAssetPath("/objects-v3/paneel_v6.glb"),
  ) as PaneelGLTFResult;

  const {
    getConfigurationBeams,
    getConfigurationWidth,
    getConfigurationHeight,
    getConfigurationColor,
    isDeMaasLayout,
    isDeWaalLayout,
    isDeIjsselLayout,
    isNotMaasWaalIjsselLayout,
    selectedDoor,
    layout,
    rotateDeWaal23,
    mainDeWaal,
    isDeMaas2DubbelLayout,
  } = useConfigurationOptionsContext();

  //Chosen material
  const chosenMaterial = materials[getConfigurationColor()];

  const layoutCode = layout?.code || "";
  const selectedDoorCode = selectedDoor?.code || "";
  const isPanelSecondary =
    panelSecondaryDoors.includes(selectedDoorCode) &&
    !panelMainLayouts.includes(layoutCode);

  const showDeMaasLayout = isDeMaasLayout && !isPanelSecondary;
  const showDeWaalLayout =
    isDeWaalLayout &&
    !isPanelSecondary &&
    !deWaal2PanelCodes.includes(layoutCode);
  const showDeWaalLayoutBeam =
    isDeWaalLayout && deWaal2PanelCodes.includes(layoutCode);
  const isDeIjssel1Layout =
    layoutCode === "deijssel1" || layoutCode === "deijssel1dubbel";
  const isOriginalDeIjsselLayout = isDeIjsselLayout && !isDeIjssel1Layout;
  const showDeIjsselCurve = isOriginalDeIjsselLayout && !isPanelSecondary;
  const showDeIjsselLowBeam = isOriginalDeIjsselLayout && isPanelSecondary;
  const showDeMaas2DubbelBeam = isDeMaas2DubbelLayout && isPanelSecondary;
  const doorWidth = getConfigurationWidth(index);
  const doorHeight = getConfigurationHeight(index);

  const showBeam =
    showDeIjsselLowBeam || showDeWaalLayoutBeam || isDeIjssel1Layout;
  const existingBeamCenterMm = showDeWaalLayoutBeam ? 2230 : 1880;
  const beamCenterHeightMm = isDeIjssel1Layout
    ? deIjsselGeometry.upperCenterMm
    : existingBeamCenterMm;
  const beamOffsetMm = isDeIjssel1Layout ? deIjsselGeometry.offsetMm : -5;

  // Determine if we need to rotate De Waal paneel
  // DeWaal2 - DeWaal 3
  const rotatePaneelDeWaal23 = rotateDeWaal23 && index === 3;
  const rotatePaneelDeWaal4 = index === 0 && layoutCode === "dewaal4dubbel";

  const isMainDeWaalLayout = mainDeWaal?.code === layoutCode;
  const rotateDeWall =
    !isMainDeWaalLayout && !rotatePaneelDeWaal23 && !rotatePaneelDeWaal4;

  return (
    <group
      dispose={null}
      position={position}
      scale-y={frameSettings?.allScaleY ?? 1}
      rotation={rotation}
    >
      <group scale-y={frameSettings?.generalScaleY ?? 1}>
        <Frame
          doorNodes={nodes}
          materials={materials}
          index={index}
          {...frameSettings}
        />
        <PaneelVerticalLayout
          nodes={nodes}
          material={chosenMaterial}
          width={doorWidth}
          height={doorHeight}
          semanticPosition={verticalBeamPosition}
        />
      </group>
      <Glass doorNodes={nodes} materials={materials} index={index} />
      {isNotMaasWaalIjsselLayout &&
        getConfigurationBeams(true).map((beam, i) => {
          return (
            <Beam
              id={beam.id}
              key={`paneel-beam-${i}`}
              doorNodes={nodes}
              materials={materials}
              height={beam.height / 1000}
              width={getConfigurationWidth(index)}
            />
          );
        })}

      <group rotation-y={rotateNewlayouts ? Math.PI : 0}>
        {/* De Maas */}
        {showDeMaasLayout && (
          <DeMaasLayout
            nodes={nodes}
            chosenMaterial={chosenMaterial}
            doorIndex={index}
          />
        )}

        {/* De Waal */}
        <group rotation-y={rotateDeWall ? Math.PI : 0}>
          {showDeWaalLayout && (
            <DeWaalLayout
              nodes={nodes}
              chosenMaterial={chosenMaterial}
              doorIndex={index}
            />
          )}
        </group>

        {showBeam && (
          <PaneelBeam
            doorNodes={nodes}
            material={chosenMaterial}
            widthMm={doorWidth}
            doorHeightMm={doorHeight}
            centerHeightMm={beamCenterHeightMm}
            offsetMm={beamOffsetMm}
          />
        )}

        <PaneelDeMaas2DubbelBeam
          doorNodes={nodes}
          materials={materials}
          width={doorWidth}
          visible={showDeMaas2DubbelBeam}
        />

        {/* De Ijssel */}
        <group>
          {showDeIjsselCurve && (
            <DeIjsselLayout
              nodes={nodes}
              chosenMaterial={chosenMaterial}
              doorIndex={index}
            />
          )}
        </group>
      </group>
    </group>
  );
};

export default Paneel;
